import { LitElement, TemplateResult, html } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';

import innerStyles from './outline-accordion-item.inner.pcss?inline';
import globalStyles from './outline-accordion-item.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-icon/outline-icon';

/**
 * The OutlineAccordionItem component
 * @element outline-accordion-item
 *
 * @extends LitElement
 *
 * @slot summary - The accordion item's details summary title
 * @slot icon - The accordion item's custom open/close icon
 * @slot - The accordion item's details default slot for content
 *
 * ## Documentation
 *
 * The `OutlineAccordionItem` component is a custom web component that extends `LitElement`. It is designed to be used inside the `OutlineAccordion` component and provides functionality to control its behavior, including animated expansion and collapse.
 *
 * ### Slots
 * - `summary` slot: Used to place the summary title of the accordion item.
 * - `icon` slot: Used to place a custom open/close icon for the accordion item.
 * - Default slot: Used to place the content of the accordion item.
 *
 * ### Events
 * - `accordion-item-click`: Dispatched when the summary of the `OutlineAccordionItem` is clicked.
 *
 * ### Methods
 * - `handleSummaryClick()`: Handles the click event on the summary, toggles the accordion state, and emits a custom event.
 * - `open()`: Initiates the opening of the accordion item.
 * - `expand()`: Animates the expansion of the accordion item and rotation of the icon.
 * - `shrink()`: Animates the collapse of the accordion item and rotation of the icon.
 * - `onAnimationFinish()`: Cleans up after animation completion.
 *
 * ### Properties
 * - `isOpen`: Getter that returns whether the accordion item is currently open.
 * - `isClosing`: Private property tracking if the accordion is currently closing.
 * - `isExpanding`: Private property tracking if the accordion is currently expanding.
 * - `prefersReducedMotion`: Tracks user preference for reduced motion.
 * - `animation`: Stores the current content height animation.
 * - `iconAnimation`: Stores the current icon rotation animation.
 *
 * ### Animation
 * The component uses the Web Animations API to smoothly animate the opening and closing of the accordion item,
 * as well as the rotation of the icon. Both animations are synchronized and can be cancelled if interrupted.
 * If the user prefers reduced motion, the animations are skipped.
 *
 * ### Accessibility
 * The component respects the user's preference for reduced motion, providing a more accessible experience when needed.
 */

@customElement('outline-accordion-item')
export class OutlineAccordionItem extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  // The current stored details animation
  private detailsAnimation: Animation | null = null;
  // The current stored icon animation
  private iconAnimation: Animation | null = null;
  // The current closing state of an accordion
  private isClosing = false;
  // The current expanding state of the accordion
  private isExpanding = false;
  // The current windows preference on if the user prefers reduced motion
  private prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  @query('.details')
  private detailsElement: HTMLDetailsElement;
  @query('.summary')
  private summaryElement: HTMLElement;
  @query('.content')
  private contentElement: HTMLElement;
  @query('.icon-wrapper')
  private iconWrapperElement: HTMLElement;

  @property({type: Boolean, attribute: 'start-open'})
  startOpen: boolean;

  firstUpdated() {
    if (this.startOpen) {
      this.detailsElement.setAttribute('open', 'open')
    }
  }

  handleSummaryClick(event: Event) {
    event.preventDefault();

    // Emit a custom event to signal that the component summary was clicked
    this.dispatchEvent(
      new CustomEvent('accordion-item-click', {
        bubbles: true, // Allows the event to bubble up through the DOM
        composed: true, // Allows the event to cross the shadow DOM boundary
      })
    );

    // Check if the element is being closed or is already closed
    if (this.isClosing || !this.isOpen) {
      this.open();
      // Check if the element is being opened or is already open
    } else if (this.isExpanding || this.isOpen) {
      this.shrink();
    }
  }

  get isOpen(): boolean {
    return this.detailsElement?.hasAttribute('open') ?? false;
  }

  open = () => {
    if (!this.detailsElement) return;

    // Apply a fixed height on the element
    this.detailsElement.style.height = `${this.detailsElement.offsetHeight}px`;
    // Wait for the next frame to call the expand function
    window.requestAnimationFrame(() => this.expand());
  };

  expand = () => {
    if (
      !this.detailsElement ||
      !this.summaryElement ||
      !this.contentElement ||
      !this.iconWrapperElement
    )
      return;

    this.isExpanding = true;

    const startHeight = `${this.detailsElement.offsetHeight}px`;
    const summaryHeight = this.summaryElement.offsetHeight;
    const contentHeight = this.contentElement.offsetHeight;
    const endHeight = `${summaryHeight + contentHeight}px`;

    // Test if the user prefers reduced motion and skip the animation
    if (!this.prefersReducedMotion.matches) {
      // Test if there is an animation in place
      if (this.detailsAnimation) {
        // Cancel the current animations
        this.detailsAnimation?.cancel();
        this.iconAnimation?.cancel();
      }

      // Start a new WAAPI animation
      this.detailsAnimation = this.detailsElement.animate(
        {
          height: [startHeight, endHeight],
        },
        {
          duration: 300,
          easing: 'ease-out',
        }
      );

      // Add icon rotation animation
      this.iconAnimation = this.iconWrapperElement.animate(
        {
          transform: ['rotate(0deg)', 'rotate(180deg)'],
        },
        {
          duration: 300,
          easing: 'ease-out',
          fill: 'forwards',
        }
      );

      // Set the [open] attribute on the details element
      this.detailsElement.setAttribute('open', '');

      // When the animation is complete, call onAnimationFinish()
      this.detailsAnimation.onfinish = () => this.onAnimationFinish();
      // If the animation is cancelled, isExpanding variable is set to false
      this.detailsAnimation.oncancel = () => (this.isExpanding = false);
      this.iconAnimation.onfinish = () => {
        this.iconWrapperElement.style.transform = 'rotate(180deg)';
      };
    } else {
      this.detailsElement.setAttribute('open', '');
      this.iconWrapperElement.style.transform = 'rotate(180deg)';
      this.onAnimationFinish();
    }
  };

  shrink = () => {
    if (
      !this.detailsElement ||
      !this.summaryElement ||
      !this.iconWrapperElement
    )
      return;

    this.isClosing = true;
    // Store the current height of the element
    const startHeight = `${this.detailsElement.offsetHeight}px`;
    // Calculate the height of the summary
    const endHeight = `${this.summaryElement.offsetHeight}px`;

    // Test if the user prefers reduced motion and skip the animation
    if (!this.prefersReducedMotion.matches) {
      // Test if there is an animation in place
      if (this.detailsAnimation) {
        // Cancel the current animation
        this.detailsAnimation.cancel();
      }

      // Start a new WAAPI animation for the details element
      this.detailsAnimation = this.detailsElement.animate(
        {
          height: [startHeight, endHeight],
        },
        {
          duration: 300,
          easing: 'ease-out',
        }
      );

      // Start a new WAAPI animation for the icon wrapper
      this.iconAnimation = this.iconWrapperElement.animate(
        {
          transform: ['rotate(180deg)', 'rotate(0deg)'],
        },
        {
          duration: 300,
          easing: 'ease-out',
          fill: 'forwards',
        }
      );

      this.detailsAnimation.onfinish = () => {
        this.detailsElement.removeAttribute('open');
        this.onAnimationFinish();
      };
      this.detailsAnimation.oncancel = () => (this.isClosing = false);
      this.iconAnimation.onfinish = () => {
        this.iconWrapperElement.style.transform = 'rotate(0deg)';
      };
    } else {
      this.detailsElement.removeAttribute('open');
      this.iconWrapperElement.style.transform = 'rotate(0deg)';
      this.onAnimationFinish();
    }
  };

  onAnimationFinish() {
    if (!this.detailsElement) return;

    // Clear the stored animation
    this.detailsAnimation = null;
    // Reset isClosing & isExpanding
    this.isClosing = false;
    this.isExpanding = false;
    // Reset the details element inline styles
    this.detailsElement.style.height = '';
    this.detailsElement.style.overflow = '';
  }

  render(): TemplateResult {
    return html`
      <div class="accordion-item">
        <details class="details">
          <summary @click=${this.handleSummaryClick} class="summary">
            <slot name="summary"></slot>
            <div class="icon-wrapper" aria-hidden="true">
              <slot name="icon"></slot>
            </div>
          </summary>
          <div class="content">
            <slot></slot>
          </div>
        </details>
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-accordion-item': OutlineAccordionItem;
  }
}
